import { useEffect, useId, useMemo, useRef, useState } from 'react'

import { filterSuggestions } from '../screens/catalogFilters'

type FilterComboboxProps = {
  label: string
  value: string
  options: string[]
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  maxOptions?: number
  listAllOnFocus?: boolean
}

export const FilterCombobox = ({
  label,
  value,
  options,
  onChange,
  placeholder,
  className,
  disabled = false,
  maxOptions = 12,
  listAllOnFocus = false,
}: FilterComboboxProps) => {
  const listId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  const filteredOptions = useMemo(() => {
    const query = value.trim()
    if (!query) {
      return listAllOnFocus ? options.slice(0, maxOptions) : []
    }
    return filterSuggestions(options, query, maxOptions)
  }, [listAllOnFocus, maxOptions, options, value])

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [])

  const showList = open && !disabled
  const hasQuery = value.trim().length > 0

  return (
    <div
      ref={rootRef}
      className={['filter-combobox', className].filter(Boolean).join(' ')}
    >
      <label className="echat-field">
        <span className="echat-field__label">{label}</span>
        <input
          className="echat-field__input"
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          onChange={(event) => {
            onChange(event.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
        />
      </label>
      {showList ? (
        <ul id={listId} className="filter-combobox__list" role="listbox">
          {!hasQuery && !listAllOnFocus ? (
            <li className="filter-combobox__hint">Введите название для поиска</li>
          ) : null}
          {hasQuery && filteredOptions.length === 0 ? (
            <li className="filter-combobox__hint">Ничего не найдено</li>
          ) : null}
          {filteredOptions.map((option) => (
            <li key={option}>
              <button
                type="button"
                className="filter-combobox__option"
                role="option"
                aria-selected={option === value}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange(option)
                  setOpen(false)
                }}
              >
                {option}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
